// 2026-06-25: Phase 10 — ECR list (create + approve/reject)
import { cleanCustomerId } from '../../Services/app/operatorservice';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { listEcrs, createEcr, approveEcr, rejectEcr, listBoms } from '../../Services/app/workorderservice';
import Swal from 'sweetalert2';
import '../NcLog/NcLog.css';
import '../Bom/Bom.css';

const STATUS_COLOR = {
  DRAFT: '#9ca3af', UNDER_REVIEW: '#f59e0b', APPROVED: '#16a34a',
  REJECTED: '#dc2626', CONVERTED_TO_ECN: '#2563eb',
};
const PRIORITY_COLOR = { STANDARD: '#6b7280', EXPEDITED: '#f59e0b', EMERGENCY: '#dc2626' };

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const ist = new Date(d.getTime() + 5.5 * 3600_000);
  return ist.toISOString().slice(0, 16).replace('T', ' ');
}

export default function EcrList() {
  const customerId = cleanCustomerId(localStorage.getItem('CustomerID'));
  const [rows, setRows] = useState([]);
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('OPEN');
  const [showCreate, setShowCreate] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [r, b] = await Promise.all([
        listEcrs(customerId),
        listBoms(customerId).catch(() => []),
      ]);
      setRows(r || []);
      setBoms(b || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [customerId]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return rows;
    if (filter === 'OPEN') return rows.filter((r) => ['DRAFT', 'UNDER_REVIEW'].includes(r.status));
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const counts = useMemo(() => ({
    ALL: rows.length,
    OPEN: rows.filter((r) => ['DRAFT', 'UNDER_REVIEW'].includes(r.status)).length,
    APPROVED: rows.filter((r) => r.status === 'APPROVED').length,
    REJECTED: rows.filter((r) => r.status === 'REJECTED').length,
    CONVERTED_TO_ECN: rows.filter((r) => r.status === 'CONVERTED_TO_ECN').length,
  }), [rows]);

  return (
    <div className="bom-page pages" style={{ paddingTop: 32 }}>
      <div className="nc-header">
        <div>
          <h5 style={{ margin: 0 }}>Engineering Change Requests (ECR)</h5>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            Anyone can raise an ECR · Engineering reviews → approves → converts to ECN
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="nc-btn-secondary" onClick={fetchAll}>Refresh</button>
          <button className="nc-btn-primary" onClick={() => setShowCreate(true)}>+ New ECR</button>
        </div>
      </div>

      <div className="nc-tabs">
        {['OPEN', 'APPROVED', 'REJECTED', 'CONVERTED_TO_ECN', 'ALL'].map((t) => (
          <button key={t} className={`nc-tab ${filter === t ? 'nc-tab-active' : ''}`} onClick={() => setFilter(t)}>
            {t === 'CONVERTED_TO_ECN' ? 'CONVERTED' : t} <span className="nc-tab-count">{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div> :
        filtered.length === 0 ? <div style={{ padding: 30, color: '#9ca3af' }}>No ECRs in this view.</div> : (
        <div className="nc-grid ecn-list">
          {filtered.map((r) => (
            <div key={r.id} className="nc-card" style={{ borderLeftColor: PRIORITY_COLOR[r.priority] }}>
              <div className="nc-card-head">
                <div>
                  <div className="nc-num">{r.ecr_number}</div>
                  <div className="nc-meta">{fmtTime(r.created_ts)}</div>
                </div>
                <span className="nc-pill" style={{ background: STATUS_COLOR[r.status] }}>{r.status.replace('_', ' ')}</span>
              </div>
              <div style={{ fontWeight: 700, color: '#111827', fontSize: 13, margin: '4px 0' }}>{r.title}</div>
              <div className="nc-row"><span>Part</span><strong>{r.affected_part_number} (rev {r.affected_bom_revision || '?'})</strong></div>
              <div className="nc-row"><span>Type / Priority</span><strong>{r.change_type} · {r.priority}</strong></div>
              <div className="nc-row"><span>Requested by</span><strong>{r.requested_by}{r.requested_by_dept && ` · ${r.requested_by_dept}`}</strong></div>
              <div className="nc-desc">{r.reason}</div>
              {r.proposed_change && <div style={{ fontSize: 12, color: '#374151', marginTop: 4 }}><em>Proposed:</em> {r.proposed_change}</div>}
              {r.converted_to_ecn_number && <div className="nc-rw-link">→ {r.converted_to_ecn_number}</div>}
              {['DRAFT', 'UNDER_REVIEW'].includes(r.status) && (
                <div className="nc-actions">
                  <button className="nc-btn-primary" onClick={() => setReviewing(r)}>Review</button>
                </div>
              )}
              {r.reviewed_by && (
                <div className="nc-dispo-notes">
                  <em>Reviewed by:</em> {r.reviewed_by} · {r.review_notes || ''}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <EcrCreateDialog customerId={customerId} boms={boms} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll(); }} />
      )}
      {reviewing && (
        <EcrReviewDialog ecr={reviewing} customerId={customerId} onClose={() => setReviewing(null)} onDone={() => { setReviewing(null); fetchAll(); }} />
      )}
    </div>
  );
}

function EcrCreateDialog({ customerId, boms, onClose, onCreated }) {
  const [form, setForm] = useState({
    affected_part_number: '', title: '', reason: '', proposed_change: '',
    change_type: 'BOM', priority: 'STANDARD', requested_by: '', requested_by_dept: '',
  });
  const [saving, setSaving] = useState(false);

  // Active BOM parts (unique)
  const partOptions = useMemo(() => {
    const m = new Map();
    (boms || []).filter((b) => b.status === 'ACTIVE').forEach((b) => m.set(b.part_number, b));
    return Array.from(m.values());
  }, [boms]);

  const submit = async () => {
    if (!form.affected_part_number || !form.title || !form.reason || !form.requested_by) {
      Swal.fire('Missing', 'Part, title, reason, requested-by required', 'warning'); return;
    }
    setSaving(true);
    try {
      await createEcr({ ...form, customer_id: customerId });
      Swal.fire({ icon: 'success', title: 'ECR raised', timer: 1300, showConfirmButton: false });
      onCreated();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head"><h6>New ECR</h6><button onClick={onClose}>×</button></div>
        <div className="nc-modal-body">
          <label>Affected Part *</label>
          <select value={form.affected_part_number} onChange={(e) => setForm({ ...form, affected_part_number: e.target.value })}>
            <option value="">— Pick part (active BOMs) —</option>
            {partOptions.map((b) => <option key={b.part_number} value={b.part_number}>{b.part_number} (rev {b.revision})</option>)}
          </select>
          <label>Title *</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Replace bolt X with X2 due to obsolescence" />
          <div className="nc-grid-2">
            <div>
              <label>Type</label>
              <select value={form.change_type} onChange={(e) => setForm({ ...form, change_type: e.target.value })}>
                <option>BOM</option><option>DRAWING</option><option>PROCESS</option><option>MATERIAL</option><option>OTHER</option>
              </select>
            </div>
            <div>
              <label>Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option>STANDARD</option><option>EXPEDITED</option><option>EMERGENCY</option>
              </select>
            </div>
            <div>
              <label>Requested by *</label>
              <input value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })} />
            </div>
            <div>
              <label>Department</label>
              <input value={form.requested_by_dept} onChange={(e) => setForm({ ...form, requested_by_dept: e.target.value })} />
            </div>
          </div>
          <label>Reason *</label>
          <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <label>Proposed change</label>
          <textarea rows={2} value={form.proposed_change} onChange={(e) => setForm({ ...form, proposed_change: e.target.value })} />
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-primary" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Submit ECR'}</button>
        </div>
      </div>
    </div>
  );
}

function EcrReviewDialog({ ecr, customerId, onClose, onDone }) {
  const [reviewer, setReviewer] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const act = async (action) => {
    if (!reviewer) { Swal.fire('Missing', 'Your name required', 'warning'); return; }
    setSaving(true);
    try {
      const body = { customer_id: customerId, reviewed_by: reviewer, review_notes: notes };
      if (action === 'approve') await approveEcr(ecr.id, body);
      else await rejectEcr(ecr.id, body);
      Swal.fire({ icon: 'success', title: action === 'approve' ? 'ECR approved' : 'ECR rejected', timer: 1300, showConfirmButton: false });
      onDone();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || e.message, 'error');
    }
    setSaving(false);
  };

  return (
    <div className="nc-modal-backdrop" onClick={onClose}>
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nc-modal-head"><h6>Review {ecr.ecr_number}</h6><button onClick={onClose}>×</button></div>
        <div className="nc-modal-body">
          <div className="nc-dispose-summary">
            <div><strong>{ecr.title}</strong></div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{ecr.reason}</div>
            {ecr.proposed_change && <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}><em>Proposed:</em> {ecr.proposed_change}</div>}
          </div>
          <label>Your name *</label>
          <input value={reviewer} onChange={(e) => setReviewer(e.target.value)} />
          <label>Review notes</label>
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="nc-modal-foot">
          <button className="nc-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="nc-btn-secondary" onClick={() => act('reject')} disabled={saving} style={{ background: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }}>Reject</button>
          <button className="nc-btn-primary" onClick={() => act('approve')} disabled={saving} style={{ background: '#16a34a', borderColor: '#16a34a' }}>Approve</button>
        </div>
      </div>
    </div>
  );
}
